import React, { useEffect, useState, useCallback } from 'react';
import { auth, db } from '../firebase';
import { 
  collection, query, where, getDocs, 
  deleteDoc, doc, getDoc, updateDoc, addDoc 
} from 'firebase/firestore';
import { verifyBeforeUpdateEmail } from 'firebase/auth'; 
import { useNavigate } from 'react-router-dom';

const Profile = () => {
  const navigate = useNavigate();
  
  // --- CORE STATES ---
  const [user, setUser] = useState(null);
  const [myWorkers, setMyWorkers] = useState([]);
  const [myTeam, setMyTeam] = useState([]); 
  const [myHiredWorkers, setMyHiredWorkers] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('uploads'); 
  
  // --- ADMIN & SEARCH STATES ---
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminStats, setAdminStats] = useState([]); 
  const [agentSearch, setAgentSearch] = useState(""); 
  const [commissionSearch, setCommissionSearch] = useState(""); 
  
  // --- BULK UPLOAD STATES ---
  const [bulkText, setBulkText] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);

  // --- MODAL STATES ---
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);

  const [showThekedarModal, setShowThekedarModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [targetCollection, setTargetCollection] = useState("users"); 
  const [thekedarTypeInput, setThekedarTypeInput] = useState("");

  const [showAddLabourModal, setShowAddLabourModal] = useState(false);
  const [targetThekedar, setTargetThekedar] = useState(null);
  const [labourForm, setLabourForm] = useState({ name: '', mobile: '', skill: '', wage: '', village: '', district: '', state: '' });
  
  const [labourMode, setLabourMode] = useState('manage'); 
  const [availableWorkers, setAvailableWorkers] = useState([]); 
  const [currentTeam, setCurrentTeam] = useState([]); 
  const [workerSearch, setWorkerSearch] = useState(""); 

  const ratePerJoining = 10;
  const commissionPercent = 20;

  // --- DATA FETCHING LOGIC ---

  const fetchAdminStats = useCallback(async () => {
    try {
        const usersSnap = await getDocs(collection(db, "users"));
        const usersMap = {};
        usersSnap.forEach(doc => {
            usersMap[doc.id] = { id: doc.id, ...doc.data(), count: 0, type: 'user' }; 
        });

        const workersSnap = await getDocs(collection(db, "workers"));
        workersSnap.forEach(doc => {
            const ownerId = doc.data().assignedThekedarId || doc.data().createdBy;
            if (usersMap[ownerId]) usersMap[ownerId].count += 1;
        });

        const statsArray = Object.values(usersMap);
        statsArray.sort((a, b) => b.count - a.count);
        setAdminStats(statsArray);
    } catch (error) { console.error("Admin Stats Error:", error); }
  }, []);

  const fetchAllInitialData = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      let fullUserData = { ...currentUser };

      if (userDocSnap.exists()) {
          const firestoreData = userDocSnap.data();
          fullUserData = { ...fullUserData, ...firestoreData }; 
          if (firestoreData.role === 'admin') {
              setIsAdmin(true);
              fetchAdminStats(); 
          }
      }
      setUser(fullUserData);

      // Fetch Uploads
      const q = query(collection(db, "workers"), where("createdBy", "==", currentUser.uid));
      const querySnapshot = await getDocs(q);
      setMyWorkers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'worker' })));

      // Fetch Team if Thekedar
      if (fullUserData.isThekedar) {
        const qAssigned = query(collection(db, "workers"), where("assignedThekedarId", "==", currentUser.uid));
        const assignedSnapshot = await getDocs(qAssigned);
        setMyTeam(assignedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'worker' })));
      }

      // Fetch Hired Workers
      const qHired = query(collection(db, "workers"), where("hiredBy", "==", currentUser.uid), where("isHired", "==", true));
      const hiredSnap = await getDocs(qHired);
      setMyHiredWorkers(hiredSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }
  }, [fetchAdminStats]);

  useEffect(() => {
    fetchAllInitialData().then(() => setLoading(false));
  }, [fetchAllInitialData]);

  // --- ACTIONS ---

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this worker permanently?")) {
      try {
        await deleteDoc(doc(db, "workers", id));
        fetchAllInitialData(); 
      } catch (error) { alert("Error deleting: " + error.message); }
    }
  };

  const releaseWorker = async (workerId, workerName) => {
    if (!window.confirm(`Kya aap ${workerName} ko release karna chahte hain?`)) return;
    try {
        await updateDoc(doc(db, "workers", workerId), {
            isHired: false,
            hiredBy: null,
            hiredUntil: null
        });
        alert("Worker released successfully! ✅");
        fetchAllInitialData(); 
    } catch (error) { alert("Failed to release."); }
  };

  const handleBadgeClick = (id, isThekedar, collectionName) => {
      setTargetCollection(collectionName);
      setSelectedId(id);
      if (isThekedar) {
          if(window.confirm("Remove Thekedar Badge?")) toggleThekedarBadge(id, true, null, collectionName); 
      } else {
          setThekedarTypeInput(""); 
          setShowThekedarModal(true);
      }
  };

  const confirmThekedarAssign = () => {
      if (!thekedarTypeInput.trim()) return alert("Please enter a category.");
      toggleThekedarBadge(selectedId, false, thekedarTypeInput, targetCollection);
      setShowThekedarModal(false);
      setAgentSearch(""); 
  };

  const toggleThekedarBadge = async (id, currentStatus, type, collectionName) => {
    try {
        await updateDoc(doc(db, collectionName, id), { 
            isThekedar: !currentStatus, 
            thekedarType: !currentStatus ? type : null 
        });
        fetchAllInitialData();
    } catch (error) { alert("Badge update failed."); }
  };

  // --- TEAM MANAGEMENT ---

  const openAddLabourModal = async (thekedar) => {
      setTargetThekedar(thekedar);
      setLabourForm({ name: '', mobile: '', skill: '', wage: '', village: '', district: thekedar.district || '', state: thekedar.state || '' });
      setLabourMode('manage'); 
      setShowAddLabourModal(true);

      try {
          const snap = await getDocs(collection(db, "workers"));
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setCurrentTeam(list.filter(w => w.assignedThekedarId === thekedar.id));
          setAvailableWorkers(list.filter(w => w.assignedThekedarId !== thekedar.id));
      } catch (e) { console.error(e); }
  };

  const saveNewLabour = async () => {
      if(!labourForm.name || !labourForm.mobile || !labourForm.skill) return alert("Fill all details.");
      try {
          await addDoc(collection(db, "workers"), {
              ...labourForm,
              skills: [labourForm.skill],
              imageUrl: "",
              createdBy: auth.currentUser.uid, 
              assignedThekedarId: targetThekedar.id, 
              createdAt: new Date()
          });
          alert(`✅ Labour added to ${targetThekedar.name}!`);
          fetchAllInitialData(); 
          setShowAddLabourModal(false);
      } catch (error) { alert("Error: " + error.message); }
  };

  const assignExistingWorker = async (workerId) => {
      try {
          await updateDoc(doc(db, "workers", workerId), { assignedThekedarId: targetThekedar.id });
          alert("Assigned successfully!");
          fetchAllInitialData();
          setShowAddLabourModal(false);
      } catch (error) { alert("Failed: " + error.message); }
  };

  const removeWorkerFromThekedar = async (workerId) => {
      if(!window.confirm("Remove this worker from team?")) return;
      try {
          await updateDoc(doc(db, "workers", workerId), { assignedThekedarId: null }); 
          alert("Removed from team.");
          fetchAllInitialData();
          setShowAddLabourModal(false);
      } catch (error) { alert("Failed: " + error.message); }
  };

  // --- BULK & EMAIL ---

  const handleBulkUpload = async () => {
    if (!bulkText.trim()) return alert("Please paste data.");
    setBulkLoading(true);
    const rows = bulkText.trim().split('\n');
    let count = 0;
    for (let row of rows) {
        const cols = row.split(',');
        if (cols.length >= 4) {
            try {
                await addDoc(collection(db, "workers"), {
                    name: cols[0].trim(), mobile: cols[1].trim(), skills: [cols[2].trim()], 
                    state: cols[3].trim(), district: cols[4]?.trim() || "Unknown", wage: cols[5]?.trim() || "0",
                    village: cols[6]?.trim() || "Bulk Added", experience: cols[7]?.trim() || "0",
                    imageUrl: "", createdBy: auth.currentUser.uid, createdAt: new Date()
                });
                count++;
            } catch (e) { console.error("Row fail:", row); }
        }
    }
    alert(`🎉 Added ${count} Workers!`);
    setBulkText(""); setBulkLoading(false); 
    fetchAllInitialData();
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.includes('@')) return alert("Invalid Email.");
    setUpdatingEmail(true);
    try {
        await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
        await updateDoc(doc(db, "users", auth.currentUser.uid), { email: newEmail });
        alert(`✅ Verification Link Sent!`);
        setShowEmailModal(false);
    } catch (error) { alert("Error: " + error.message); }
    setUpdatingEmail(false);
  };

  // --- HELPERS ---

  const calculateCommission = (count) => {
    const paidCount = count > 0 ? count - 1 : 0; 
    return (paidCount * ratePerJoining * commissionPercent) / 100;
  };

  const allEntitiesForSearch = [...adminStats, ...myWorkers];
  const allThekedarsList = allEntitiesForSearch.filter(u => u.isThekedar);
  
  const activeAgentsFiltered = adminStats.filter(u => {
      const isEligible = u.count >= 2;
      const matchesSearch = commissionSearch 
          ? u.name.toLowerCase().includes(commissionSearch.toLowerCase()) || u.mobile.includes(commissionSearch) 
          : true;
      return isEligible && matchesSearch;
  });

  const filteredSearchList = agentSearch 
    ? allEntitiesForSearch.filter(s => (s.name && s.name.toLowerCase().includes(agentSearch.toLowerCase())) || (s.mobile && s.mobile.includes(agentSearch)))
    : [];

  const totalAgentsCount = adminStats.filter(u => u.count > 0).length;
  const totalWorkersCount = adminStats.reduce((acc, curr) => acc + curr.count, 0);
  const totalCommissionVal = adminStats.reduce((acc, curr) => acc + calculateCommission(curr.count), 0);
  const filteredAvailWorkers = availableWorkers.filter(w => w.name.toLowerCase().includes(workerSearch.toLowerCase()) || w.mobile.includes(workerSearch));
  const isFakeEmail = user?.email?.includes('labourconnect.com');

  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-[#0f172a]">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  // =========================================================
  // 🌟🌟🌟 ADMIN VIEW 🌟🌟🌟
  // =========================================================
  if (isAdmin) {
    return (
        <div className="min-h-screen bg-[#0f172a] text-slate-100 p-4 pb-24 relative overflow-hidden">
            {/* Background FX */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-5%] right-[-5%] w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px]"></div>
                <div className="absolute bottom-[-5%] left-[-5%] w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[100px]"></div>
            </div>

            {/* MODALS */}
            {showThekedarModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-[#1e293b] border border-white/10 rounded-3xl p-8 w-full max-w-sm shadow-2xl relative">
                        <button onClick={() => setShowThekedarModal(false)} className="absolute top-4 right-4 text-slate-400 font-bold">✕</button>
                        <h3 className="text-xl font-bold text-center mb-6">Assign Thekedar Badge</h3>
                        <input type="text" placeholder="Category (e.g. Electrician)" value={thekedarTypeInput} onChange={(e) => setThekedarTypeInput(e.target.value)} className="w-full p-4 bg-[#0f172a] rounded-xl border border-slate-700 focus:border-yellow-500 outline-none mb-4" />
                        <button onClick={confirmThekedarAssign} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black py-4 rounded-xl font-bold">Confirm Badge</button>
                    </div>
                </div>
            )}

            {showAddLabourModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-[#1e293b] border border-white/10 rounded-3xl p-6 w-full max-w-md shadow-2xl relative flex flex-col max-h-[85vh]">
                        <button onClick={() => setShowAddLabourModal(false)} className="absolute top-4 right-4 text-slate-400 font-bold text-xl">✕</button>
                        <h3 className="text-xl font-bold mb-4">Team: {targetThekedar?.name}</h3>
                        <div className="flex bg-[#0f172a] rounded-xl p-1 mb-4 shrink-0">
                            <button onClick={() => setLabourMode('manage')} className={`flex-1 py-2 rounded-lg text-xs font-bold ${labourMode === 'manage' ? 'bg-yellow-500 text-black' : 'text-slate-400'}`}>Members ({currentTeam.length})</button>
                            <button onClick={() => setLabourMode('new')} className={`flex-1 py-2 rounded-lg text-xs font-bold ${labourMode === 'new' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}>+ New</button>
                            <button onClick={() => setLabourMode('existing')} className={`flex-1 py-2 rounded-lg text-xs font-bold ${labourMode === 'existing' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}>+ Existing</button>
                        </div>
                        <div className="overflow-y-auto flex-grow pr-1">
                            {labourMode === 'manage' && (
                                <div className="space-y-2">
                                    {currentTeam.map(w => (
                                        <div key={w.id} className="bg-[#0f172a] p-3 rounded-xl flex justify-between items-center border border-slate-700">
                                            <div><p className="font-bold text-sm">{w.name}</p><p className="text-[10px] text-slate-500">{w.skills[0]}</p></div>
                                            <button onClick={() => removeWorkerFromThekedar(w.id)} className="text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-1 rounded text-xs">Remove</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {labourMode === 'new' && (
                                <div className="space-y-3">
                                    <input placeholder="Name" className="w-full p-3 bg-[#0f172a] border border-slate-700 rounded-lg" onChange={(e) => setLabourForm({...labourForm, name: e.target.value})} />
                                    <input placeholder="Mobile" className="w-full p-3 bg-[#0f172a] border border-slate-700 rounded-lg" onChange={(e) => setLabourForm({...labourForm, mobile: e.target.value})} />
                                    <input placeholder="Skill" className="w-full p-3 bg-[#0f172a] border border-slate-700 rounded-lg" onChange={(e) => setLabourForm({...labourForm, skill: e.target.value})} />
                                    <button onClick={saveNewLabour} className="w-full bg-blue-600 py-3 rounded-xl font-bold">Add New Worker</button>
                                </div>
                            )}
                            {labourMode === 'existing' && (
                                <div className="space-y-2">
                                    <input type="text" placeholder="Search..." value={workerSearch} onChange={(e) => setWorkerSearch(e.target.value)} className="w-full p-3 bg-[#0f172a] border border-slate-700 rounded-lg mb-2" />
                                    {filteredAvailWorkers.map(w => (
                                        <div key={w.id} className="bg-[#0f172a] p-3 rounded-xl border border-slate-700 flex justify-between items-center">
                                            <p className="text-sm font-bold">{w.name} ({w.skills[0]})</p>
                                            <button onClick={() => assignExistingWorker(w.id)} className="bg-green-600/20 text-green-400 border border-green-500/50 px-3 py-1 rounded text-xs">Assign</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MAIN DASHBOARD */}
            <div className="max-w-7xl mx-auto relative z-10 mt-16">
                
                {/* Header Card */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[2.5rem] mb-10 flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                    <div className="flex items-center gap-6 mb-6 md:mb-0">
                        <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-3xl flex items-center justify-center text-4xl shadow-xl ring-2 ring-white/10">😎</div>
                        <div>
                            <h1 className="text-3xl font-black tracking-tight">Admin Console</h1>
                            <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mt-1">Super User Privileges</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => navigate('/add-worker')} className="px-6 py-3 bg-blue-600 rounded-2xl font-bold text-sm shadow-lg shadow-blue-500/20">+ Add Data</button>
                        <button onClick={() => auth.signOut()} className="px-6 py-3 bg-slate-800 border border-slate-700 rounded-2xl font-bold text-sm">Logout</button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    {[
                        {label: 'Active Agents', val: totalAgentsCount, color: 'blue'},
                        {label: 'Total Workers', val: totalWorkersCount, color: 'purple'},
                        {label: 'Commission', val: `₹${totalCommissionVal}`, color: 'emerald'}
                    ].map((s, i) => (
                        <div key={i} className="bg-slate-800/40 backdrop-blur-md p-8 rounded-[2rem] border border-white/5 shadow-lg">
                            <h3 className={`text-${s.color}-400 text-xs font-bold uppercase mb-2`}>{s.label}</h3>
                            <p className="text-5xl font-black text-white">{s.val}</p>
                        </div>
                    ))}
                </div>

                {/* THEKEDAR CONSOLE */}
                <div className="mb-12">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-8 w-1.5 bg-yellow-500 rounded-full shadow-[0_0_15px_rgba(234,179,8,0.5)]"></div>
                        <h3 className="text-2xl font-bold">Thekedar Console</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {allThekedarsList.map(t => (
                            <div key={t.id} className="bg-[#1e293b]/80 p-5 rounded-3xl border border-yellow-500/20 shadow-xl relative overflow-hidden group">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-14 h-14 bg-gradient-to-br from-yellow-300 to-yellow-600 rounded-2xl flex items-center justify-center text-2xl text-black font-bold">👑</div>
                                    <div className="min-w-0">
                                        <h4 className="font-bold text-lg truncate">{t.name}</h4>
                                        <span className="text-yellow-400 text-[10px] font-bold uppercase">{t.thekedarType}</span>
                                    </div>
                                </div>
                                <button onClick={() => openAddLabourModal(t)} className="w-full bg-blue-600 py-2.5 rounded-xl font-bold text-xs mb-2">⚙️ Manage Team</button>
                                <button onClick={() => handleBadgeClick(t.id, true, t.type === 'worker' ? 'workers' : 'users')} className="w-full bg-red-500/10 text-red-400 py-2.5 rounded-xl font-bold text-xs border border-red-500/30">Remove Badge</button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* COMMISSION TABLE */}
                <div className="bg-[#1e293b]/60 backdrop-blur-xl border border-white/5 rounded-[2.5rem] p-8 mb-12 shadow-2xl">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                        <h3 className="text-2xl font-bold">High Performers (2+)</h3>
                        <input type="text" placeholder="Search Agent Name/Mobile..." value={commissionSearch} onChange={(e) => setCommissionSearch(e.target.value)} className="w-full md:w-80 bg-[#0f172a] border border-slate-700 text-white px-5 py-3 rounded-2xl outline-none focus:border-emerald-500 transition-all" />
                    </div>
                    <div className="overflow-x-auto rounded-2xl">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="bg-[#0f172a] text-slate-400 text-xs uppercase tracking-wider">
                                    <th className="p-5 font-bold">Agent</th>
                                    <th className="p-5 font-bold text-center">Count</th>
                                    <th className="p-5 font-bold text-right">Commission</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                                {activeAgentsFiltered.map(agent => (
                                    <tr key={agent.id} className="hover:bg-white/5">
                                        <td className="p-5 font-bold text-slate-200">{agent.name}</td>
                                        <td className="p-5 text-center text-blue-400 font-bold">{agent.count}</td>
                                        <td className="p-5 text-right text-emerald-400 font-bold">₹{calculateCommission(agent.count)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* FIND & PROMOTE */}
                <div className="bg-[#1e293b]/60 border border-white/5 rounded-[2.5rem] p-8 mb-12 shadow-2xl">
                    <h3 className="text-2xl font-bold mb-6">Promote to Thekedar</h3>
                    <input type="text" placeholder="Search anyone to make them Thekedar..." value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} className="w-full bg-[#0f172a] border border-slate-700 text-white px-6 py-4 rounded-2xl outline-none focus:border-blue-500 text-lg mb-6" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredSearchList.map(res => (
                            <div key={res.id} className="bg-[#0f172a] p-4 rounded-2xl border border-slate-700 flex justify-between items-center transition group">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0 ${res.isThekedar ? 'bg-yellow-500 text-black' : 'bg-slate-700'}`}>{res.name?.charAt(0)}</div>
                                    <p className="font-bold truncate">{res.name}</p>
                                </div>
                                <button onClick={() => handleBadgeClick(res.id, res.isThekedar, res.type === 'worker' ? 'workers' : 'users')} className={`text-xs px-4 py-2 rounded-xl font-bold border ${res.isThekedar ? 'border-red-500 text-red-400' : 'border-yellow-500 text-yellow-400'}`}>
                                    {res.isThekedar ? 'Remove' : 'Promote'}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* BULK UPLOAD */}
                <div className="bg-gradient-to-r from-slate-900 to-[#0f172a] border border-blue-500/30 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                    <h3 className="text-2xl font-bold mb-2">🚀 Bulk Injection</h3>
                    <p className="text-slate-400 text-xs mb-6 font-mono bg-black/30 p-3 rounded-lg border border-white/5 inline-block">Format: <span className="text-yellow-400">Name, Mobile, Skill, State, District, Wage, Village, Experience</span></p>
                    <textarea className="w-full h-40 bg-black/50 border border-slate-700 rounded-2xl p-5 text-sm font-mono text-emerald-400 focus:outline-none focus:border-blue-500" placeholder="Ramesh, 9876543210, Plumber, Delhi, South Delhi, 500, Rampur, 5" value={bulkText} onChange={(e) => setBulkText(e.target.value)}></textarea>
                    <button onClick={handleBulkUpload} disabled={bulkLoading} className="mt-6 px-8 py-4 bg-blue-600 rounded-xl font-bold shadow-lg transform hover:scale-105 transition-all">⚡ Start Batch Upload</button>
                </div>
            </div>
        </div>
    );
  }

  // =========================================================
  // 👤 USER VIEW 👤
  // =========================================================
  return (
    <div className="min-h-screen bg-slate-50 p-6 pb-24 pt-24" style={{paddingTop : '100px'}}>
      
      {showEmailModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl p-8 w-full max-w-sm relative shadow-2xl">
                <button onClick={() => setShowEmailModal(false)} className="absolute top-4 right-4 text-slate-400 font-bold">✕</button>
                <h3 className="text-xl font-bold mb-4 text-slate-800">Verify Email</h3>
                <input type="email" placeholder="Email ID" value={newEmail} onChange={e => setNewEmail(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl mb-4 border border-slate-200" />
                <button onClick={handleUpdateEmail} disabled={updatingEmail} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg">Send Link</button>
            </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Profile Card */}
        <div className="bg-white rounded-[2.5rem] p-8 shadow-xl mb-8 text-center border border-white relative overflow-hidden">
          {user?.isThekedar && (
              <div className="bg-yellow-50/80 border-b border-yellow-100 p-4 -mt-8 -mx-8 mb-8 flex flex-col items-center">
                  <div className="text-4xl mb-2">👑</div>
                  <p className="text-yellow-800 font-black text-xl tracking-tight">Verified Contractor</p>
                  <p className="text-yellow-700 text-xs font-bold uppercase bg-yellow-100 px-4 py-1 rounded-full mt-2 border border-yellow-200">{user.thekedarType} Specialist</p>
              </div>
          )}
          <div className="w-28 h-28 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-5xl text-white mx-auto mb-6 shadow-2xl border-4 border-white">👤</div>
          <h2 className="text-3xl font-black text-slate-800">{user?.name || "User"}</h2>
          <p className="font-medium mt-2 text-sm px-4 py-1.5 rounded-full inline-block text-slate-500 bg-slate-50 border border-slate-100">{user?.email}</p>
          {isFakeEmail && (
             <div className="mt-6 bg-orange-50 border border-orange-100 p-5 rounded-2xl max-w-sm mx-auto shadow-sm">
                <button onClick={() => setShowEmailModal(true)} className="w-full py-2.5 bg-orange-500 text-white rounded-xl text-xs font-bold shadow-md hover:bg-orange-600 transition">➕ Add Recovery Email</button>
             </div>
          )}
          <div className="mt-8 flex gap-4 justify-center">
             <button onClick={() => navigate('/add-worker')} className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-xl shadow-blue-500/20 hover:bg-blue-700 transition-all">➕ Add Worker</button>
             <button onClick={() => auth.signOut()} className="px-8 py-3 bg-white border-2 border-slate-100 text-slate-600 rounded-2xl font-bold text-sm hover:bg-slate-50 transition-all">Logout</button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-10 overflow-x-auto">
            <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200 inline-flex flex-nowrap whitespace-nowrap">
                <button onClick={() => setActiveTab('uploads')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'uploads' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:text-blue-600'}`}>📂 My Uploads</button>
                {user?.isThekedar && (
                    <button onClick={() => setActiveTab('team')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'team' ? 'bg-yellow-500 text-black shadow-md' : 'text-slate-500 hover:text-yellow-600'}`}>👷‍♂️ My Team</button>
                )}
                <button onClick={() => setActiveTab('hired')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'hired' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-500 hover:text-emerald-600'}`}>🤝 My Hires</button>
            </div>
        </div>

        {/* --- DYNAMIC TAB CONTENT --- */}
        
        {activeTab === 'uploads' && (
            <div className="animate-fade-in">
                <h3 className="text-2xl font-bold text-slate-800 mb-6 pl-2 flex items-center gap-3"><span className="w-1.5 h-8 bg-blue-600 rounded-full"></span> My Personal Uploads</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {myWorkers.map(w => (
                      <div key={w.id} className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5 hover:shadow-xl transition-all group">
                        <img src={w.imageUrl || "https://cdn-icons-png.flaticon.com/512/9187/9187604.png"} alt="" className="w-16 h-16 rounded-2xl object-cover bg-slate-50" />
                        <div className="flex-1"><h4 className="font-bold text-slate-800 text-lg group-hover:text-blue-600 transition">{w.name}</h4><span className="text-[10px] bg-blue-50 text-blue-600 px-2.5 py-1 rounded-lg font-bold uppercase mt-1 inline-block border border-blue-100">{w.skills[0]}</span></div>
                        <button onClick={() => handleDelete(w.id)} className="p-3 bg-red-50 text-red-500 rounded-2xl hover:bg-red-500 hover:text-white transition shadow-sm">🗑️</button>
                      </div>
                  ))}
                  {myWorkers.length === 0 && <p className="col-span-full text-center text-slate-400 py-10 italic bg-white rounded-3xl border-2 border-dashed">No workers uploaded yet.</p>}
                </div>
            </div>
        )}

        {activeTab === 'team' && (
            <div className="animate-fade-in">
                <h3 className="text-2xl font-bold text-slate-800 mb-6 pl-2 flex items-center gap-3"><span className="w-1.5 h-8 bg-yellow-500 rounded-full"></span> Assigned Team</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {myTeam.map(w => (
                      <div key={w.id} className="bg-yellow-50/50 p-5 rounded-3xl shadow-sm border border-yellow-100 flex items-center gap-5 group">
                        <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center text-2xl shadow-inner">👷‍♂️</div>
                        <div className="flex-1">
                            <h4 className="font-bold text-slate-800 text-lg">{w.name}</h4>
                            <p className="text-xs text-slate-500 font-bold">{w.skills[0]} • {w.mobile}</p>
                            <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-md font-bold uppercase mt-2 inline-block">Team Member</span>
                        </div>
                      </div>
                  ))}
                  {myTeam.length === 0 && <p className="col-span-full text-center text-slate-400 py-10 italic bg-white rounded-3xl border-2 border-dashed border-yellow-100">No members in your team yet.</p>}
                </div>
            </div>
        )}

        {activeTab === 'hired' && (
            <div className="animate-fade-in">
                <h3 className="text-2xl font-bold text-slate-800 mb-6 pl-2 flex items-center gap-3"><span className="w-1.5 h-8 bg-emerald-600 rounded-full"></span> Active Hires</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {myHiredWorkers.map(w => (
                      <div key={w.id} className="bg-emerald-50 p-5 rounded-3xl border border-emerald-100 flex items-center gap-5 relative group transition-all">
                        <div className="w-16 h-16 rounded-2xl bg-emerald-200 flex items-center justify-center text-2xl shadow-inner">🤝</div>
                        <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-slate-800 text-lg truncate">{w.name}</h4>
                            <p className="text-xs text-emerald-700 font-bold uppercase mb-1">{w.skills[0]}</p>
                            {w.hiredUntil && (
                                <p className="text-[10px] text-slate-500 font-bold">End: <span className="text-red-500">{new Date(w.hiredUntil.toMillis()).toLocaleDateString()}</span></p>
                            )}
                        </div>
                        <button onClick={() => releaseWorker(w.id, w.name)} className="bg-white border border-emerald-200 text-emerald-600 px-4 py-2 rounded-xl text-[10px] font-black shadow-sm hover:bg-emerald-600 hover:text-white transition-all z-10 shrink-0">RELEASE</button>
                      </div>
                  ))}
                  {myHiredWorkers.length === 0 && (
                      <div className="col-span-full text-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-200">
                          <p className="text-slate-400 font-bold">Aapne abhi tak kisi worker ko hire nahi kiya hai.</p>
                      </div>
                  )}
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Profile;